/**
 * CAOS-580 — where a person's customised app navigation is kept.
 *
 * The Customize Navigation popup lets someone reorder, hide and re-add the tabs of the app they are
 * in. That arrangement used to live only in the frame's memory, so it was gone on the next load —
 * while the popup told the person it had been saved. This module is the store that makes the promise
 * true.
 *
 * ── WHERE IT IS KEPT ─────────────────────────────────────────────────────────────────────────────
 * CAOS-470 — against the PERSON, as the preference `nav.arrangement.<app>` (`person-preferences.ts`,
 * kept by the kernel). CAOS-580 first kept it in browser storage, which followed the person across
 * reloads on one browser and not to another; it now follows them to any browser they sign in on,
 * which is what "saved to your profile" says. An org default for an app fills in where the person
 * has not arranged it themselves.
 *
 * ── SHAPE ────────────────────────────────────────────────────────────────────────────────────────
 * One entry per app, holding BOTH halves of the arrangement:
 *   • `visible` — the tab keys the person wants, in the order they want them;
 *   • `hidden`  — the tab keys they explicitly hid.
 * Both are needed. With only the visible list, a tab the app publishes LATER is indistinguishable
 * from one the person hid, so the workspace would have to either swallow every new tab or resurrect
 * every hidden one. Recording the hiding as its own fact settles it: a tab nobody has an opinion
 * about is new, and new tabs show up.
 *
 * Every read tolerates a missing or wrong-shaped entry by returning "no preference" — a bad value
 * must degrade to the app's published navigation, never to a broken tab row.
 */

import { preference, preferenceKeys, setPreference } from './person-preferences.js';

/** Preference key for one app's arrangement, beside `rail.pinned.<source>` (CAOS-327). */
const KEY_PREFIX = 'nav.arrangement.';

/** A person's arrangement of one app's navigation. */
export interface NavArrangement {
  /** The tab keys they want to see, in the order they want them. */
  readonly visible: readonly string[];
  /** The tab keys they explicitly hid (so a later-published tab is not mistaken for one of these). */
  readonly hidden: readonly string[];
}

function preferenceKey(appKey: string): string {
  return `${KEY_PREFIX}${appKey}`;
}

function asStringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/** Read the arrangement this person made for an app, or `undefined` when they have not made one. */
export function loadNavArrangement(appKey: string): NavArrangement | undefined {
  if (!appKey) return undefined;
  const stored = preference(preferenceKey(appKey));
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return undefined;
  const record = stored as Record<string, unknown>;
  const visible = asStringList(record.visible);
  const hidden = asStringList(record.hidden);
  // An entry that names nothing at all is not a preference — treat it as absent so the app's
  // published navigation shows rather than an empty tab row.
  if (visible.length === 0 && hidden.length === 0) return undefined;
  return { visible, hidden };
}

/** Remember an arrangement. It applies on screen at once; storing it never blocks that. */
export function saveNavArrangement(appKey: string, arrangement: NavArrangement): void {
  if (!appKey) return;
  void setPreference(preferenceKey(appKey), { visible: [...arrangement.visible], hidden: [...arrangement.hidden] });
}

/** Forget one app's arrangement (Reset to Default), so it falls back to the published navigation. */
export function clearNavArrangement(appKey: string): void {
  if (!appKey) return;
  void setPreference(preferenceKey(appKey), null);
}

/** Forget EVERY app's arrangement — what "Reset personalization" on the profile means for navigation. */
export function clearAllNavArrangements(): void {
  for (const key of preferenceKeys(KEY_PREFIX)) void setPreference(key, null);
}

/** One published tab, reduced to what an arrangement is expressed over. */
export interface PublishedTab {
  readonly key: string;
}

/**
 * Apply an arrangement to an app's PUBLISHED tabs, in one place so the tab row, the popup's current
 * list and its available list can never disagree.
 *
 * The published tabs are the authority on what EXISTS; the arrangement is the authority on order and
 * on what the person wants to see. So:
 *   • a remembered tab the app no longer publishes is dropped (never a dead tab);
 *   • a published tab the person hid stays hidden, and stays offered under Available Items;
 *   • a published tab the arrangement says nothing about is NEW, and is appended so it shows up.
 */
export function applyNavArrangement<T extends PublishedTab>(
  published: readonly T[],
  arrangement: NavArrangement | undefined,
): { visible: T[]; available: T[] } {
  if (!arrangement) return { visible: [...published], available: [] };
  const byKey = new Map(published.map((t) => [t.key, t] as const));
  const hidden = new Set(arrangement.hidden);

  const visible: T[] = [];
  const placed = new Set<string>();
  for (const key of arrangement.visible) {
    const tab = byKey.get(key);
    if (tab && !placed.has(key)) {
      visible.push(tab);
      placed.add(key);
    }
  }
  // Anything published that the arrangement has no opinion about is new — show it, at the end.
  for (const tab of published) {
    if (!placed.has(tab.key) && !hidden.has(tab.key)) {
      visible.push(tab);
      placed.add(tab.key);
    }
  }
  const available = published.filter((t) => !placed.has(t.key));
  return { visible, available };
}

/**
 * Turn the popup's announcement (the new VISIBLE set) back into a storable arrangement, against what
 * the app publishes. Whatever is published and not visible is hidden — which is what hiding a tab in
 * the popup means.
 */
export function arrangementFrom(
  published: readonly PublishedTab[],
  visibleKeys: readonly string[],
): NavArrangement {
  const publishedKeys = new Set(published.map((t) => t.key));
  const visible = visibleKeys.filter((k) => publishedKeys.has(k));
  const seen = new Set(visible);
  return { visible, hidden: published.map((t) => t.key).filter((k) => !seen.has(k)) };
}
